/**
 * Admin Panel - System Health: service and OAuth health schemas.
 *
 * The bodies of the internal/external service health endpoints and the OAuth
 * token health endpoints. `ZAdminHealth` (`../zadmin-health.ts`) is where they
 * are bound to routes, and re-exports them.
 */

import { z } from "zod";

/**
 * Health status enum
 */
export const HealthStatusSchema = z.enum(["healthy", "degraded", "down", "pending"]);

/**
 * Individual service health
 */
export const ServiceHealthSchema = z.object({
  service: z.string(),
  status: HealthStatusSchema,
  responseTime: z.number().optional(),
  error: z.string().optional(),
  details: z.record(z.string(), z.unknown()).optional()
});

/**
 * Health check response (internal/external services)
 */
export const HealthCheckResponseSchema = z.object({
  status: HealthStatusSchema,
  timestamp: z.string(),
  services: z.array(ServiceHealthSchema),
  summary: z.object({
    total: z.number(),
    healthy: z.number(),
    degraded: z.number(),
    down: z.number(),
    pending: z.number().optional()
  })
});

/**
 * The refresh endpoint's answer: the same snapshot the read endpoint returns,
 * plus whether THIS call actually asked the providers again.
 *
 * 🔴 `probed: false` IS THE REFRESH DECLINING TO SPEND PROVIDER BUDGET, AND IT
 * HAS TO SAY SO. Every live sweep sends billable requests to the model
 * providers, so ad-hoc sweeps share one cooldown with the manual cron trigger;
 * a call inside it returns the cached snapshot unchanged. Without this field
 * that snapshot is indistinguishable from a fresh one, and "Refresh" would
 * report a re-check that never happened.
 */
export const RefreshExternalHealthResponseSchema = HealthCheckResponseSchema.extend({
  probed: z.boolean()
});

/**
 * OAuth token health check result
 */
export const OAuthTokenHealthResultSchema = z.object({
  totalChecked: z.number(),
  refreshed: z.number(),
  failed: z.number(),
  needsReauth: z.number(),
  durationMs: z.number(),
  timestamp: z.string()
});

/**
 * OAuth token health summary
 */
export const OAuthTokenHealthSummarySchema = z.object({
  lastCheck: OAuthTokenHealthResultSchema.nullable(),
  totalConnections: z.number(),
  connectionsNeedingReauth: z.number(),
  connectionsPendingRefresh: z.number()
});
